import { inject, Injectable } from '@angular/core';
import { LogInstanceToken } from '@evorait/pace-logging';
import { Observable, of } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import {
    NotificationStatusInterface,
    NotificationStatusListModel,
} from '../../interfaces/notification-status.interface';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';
import { NotificationStatusModel } from '../../models/low-level/notification-status.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { STATUS_ORDER, StatusModel } from '../../models/low-level/status.model';
import { StorageNotificationStatusService } from './storage-notification-status.service';
import { EventService } from '../event/event.service';
import { isDefined } from '../../utilities/is-defined';
import { LogUtilities } from '../../utilities/log-utilities';

// Create
const MAX_AGE_DAYS = 14;
const DATE_MAX_AGE_COMPLETE_NOTIFICATON = new Date();
const TIMESTAMP_MAX_AGE_COMPLETE_NOTIFICATION = DATE_MAX_AGE_COMPLETE_NOTIFICATON.setDate(
    DATE_MAX_AGE_COMPLETE_NOTIFICATON.getDate() - MAX_AGE_DAYS,
);

@Injectable({
    providedIn: 'root',
})
export class NotificationStatusService implements NotificationStatusInterface {
    private notificationStatusList: NotificationStatusModel[] = [];

    private readonly log = inject(LogInstanceToken);
    private readonly storageNotificationStatusService = inject(StorageNotificationStatusService);
    private readonly eventService = inject(EventService);

    public constructor() {
        this.eventService.synchronizationRequested$.pipe(mergeMap(() => this.init())).subscribe();
    }

    public init(): Observable<void> {
        return this.syncNotificationStatusList().pipe(
            mergeMap(() => this.loadRecentlyCompletedNotifications()),
            mergeMap((completedNotificationState) =>
                this.saveRecentlyCompletedNotifications(completedNotificationState),
            ),
        );
    }

    public getLatestNotificationStatusForId(notificationId: NotificationIdentifierModel): StatusModel | undefined {
        const notificationStatusModel = this.getNotificationStatusById(notificationId);

        return notificationStatusModel && this.getLatestNotificationStatus(notificationStatusModel);
    }

    private getLatestNotificationStatus(notificationStatus: NotificationStatusModel): StatusModel | undefined {
        if (notificationStatus.status.length === 0) {
            return;
        }

        notificationStatus.status.sort((a: StatusModel, b: StatusModel) => this.sortStatusModel(a, b));
        return notificationStatus.status[notificationStatus.status.length - 1];
    }

    public saveNotificationStatus(
        notificationId: NotificationIdentifierModel,
        newStatus: StatusModel,
    ): Observable<boolean> {
        const index = this.findNotificationModelIndexById(notificationId);

        const isExistingStatus = this.notificationStatusList[index].status
            .map((status) => status.statusCode)
            .includes(newStatus.statusCode);

        // Added a guard to ensure no same/old status gets saved again
        if (isExistingStatus) {
            return of(false);
        }
        this.notificationStatusList[index].status.push(newStatus);
        return this.saveNotificationStatusList().pipe(map(() => true));
    }

    public addMissingStatusToList(notificationId: NotificationIdentifierModel, newStatus: StatusModel): void {
        const index = this.findNotificationModelIndexById(notificationId);

        // search if there is already an entry with this status. If so, update the entry with times from backend.
        const statusInQuestion = this.notificationStatusList[index].status.some((statusModel: StatusModel) => {
            return statusModel.statusCode === newStatus.statusCode;
        });
        if (statusInQuestion) {
            this.log.debug(
                `${this.constructor.name}: addMissingStatusToLocalStorage: Debug-Info:  ${newStatus.statusCode} has not been added because its present in local storage. TaskSet Status -> ${newStatus.toString()}, Local Storage Status -> ${statusInQuestion.toString()}`,
            );
            return;
        }
        this.log.debug(
            `${this.constructor.name}: addMissingStatusToLocalStorage: Adding status ${newStatus.toString()}`,
        );
        this.notificationStatusList[index].status.push(newStatus);
    }

    /**
     * This method is to update the memory variable of Notification status model list
     *
     * @param notificationStatusList
     */
    public setNotificationStatusList(notificationStatusList: NotificationStatusModel[]): void {
        this.notificationStatusList = notificationStatusList;
    }

    /**
     * This method is to store the Notification status model list to file and concurrent storage
     */

    public saveNotificationStatusList(): Observable<void> {
        const saveNotificationStatusList: NotificationStatusListModel = {
            notificationStatusList: this.notificationStatusList,
            lastSavedTimestamp: Date.now(),
        };
        return this.storageNotificationStatusService.saveAllNotificationStatusModelToStorage(
            saveNotificationStatusList,
        );
    }

    /**
     * This method reads the notification status model list from file and concurrent storage
     */
    public syncNotificationStatusList(): Observable<NotificationStatusListModel> {
        return this.storageNotificationStatusService
            .getAllNotificationsStatusModelFromStorage()
            .pipe(take(1))
            .pipe(
                tap(
                    (notificationStatusList) =>
                        (this.notificationStatusList = notificationStatusList.notificationStatusList),
                ),
            );
    }

    public loadRecentlyCompletedNotifications(): Observable<NotificationCompletedListModel> {
        return this.storageNotificationStatusService.loadCompletedNotificationsToStorage().pipe(
            map((completedNotifications) => {
                this.log.debug(
                    `${this.constructor.name}: loadRecentlyCompletedNotifications`,
                    LogUtilities.getLogOutputCompletedNotifications(completedNotifications),
                );
                const cleanedCompletedNotifications = this.cleanRecentlyCompletedNotifications(completedNotifications);
                this.eventService.updateCompletedNotifications(cleanedCompletedNotifications);
                return cleanedCompletedNotifications;
            }),
        );
    }

    public saveRecentlyCompletedNotifications(
        completedNotificationState: NotificationCompletedListModel,
    ): Observable<void> {
        return this.storageNotificationStatusService.saveCompletedNotificationsToStorage(completedNotificationState);
    }

    public addNotificationToRecentlyCompletedList(notification: NotificationModel): Observable<void> {
        this.log.debug(`${this.constructor.name}: addNotificationToRecentlyCompletedList`, {
            notificationId: notification.identifier,
        });
        return this.loadRecentlyCompletedNotifications().pipe(
            map((completedNotificationState) => {
                completedNotificationState.completedNotifications[`${notification.identifier.toString()}`] = {
                    id: notification.identifier,
                    status: notification.updatedStatus.statusCode,
                    timestampCompleted: Date.now(),
                };
                completedNotificationState.lastSavedTimestamp = Date.now();

                this.eventService.updateCompletedNotifications(completedNotificationState);
                return completedNotificationState;
            }),
            mergeMap((completedNotificationState) =>
                this.saveRecentlyCompletedNotifications(completedNotificationState),
            ),
        );
    }

    public isNotificationCompleted(notificationId: NotificationIdentifierModel): Observable<boolean> {
        return this.loadRecentlyCompletedNotifications().pipe(
            map((completedNotifState) =>
                isDefined(completedNotifState.completedNotifications[notificationId.toString()]),
            ),
        );
    }

    /**
     * Removes completed notification identifiers that are older than TIMESTAMP_TWO_WEEKS_AGO.
     *
     * @param dirtyNotifList
     * @private
     */
    private cleanRecentlyCompletedNotifications(
        dirtyNotifList: NotificationCompletedListModel,
    ): NotificationCompletedListModel {
        return {
            completedNotifications: Object.fromEntries(
                Object.entries(dirtyNotifList.completedNotifications).filter(
                    ([, completedNotif]) =>
                        completedNotif.timestampCompleted >= TIMESTAMP_MAX_AGE_COMPLETE_NOTIFICATION,
                ),
            ),
            lastSavedTimestamp: dirtyNotifList.lastSavedTimestamp,
        };
    }

    /**
     * Returns all Notification status for particular notification identifier
     *
     * @param notificationId
     */
    private getAllNotificationStatus(notificationId: NotificationIdentifierModel): StatusModel[] {
        const notificationStatus = this.getNotificationStatusById(notificationId);

        return notificationStatus.status || [];
    }

    /**
     * This method is return 'Index' of Notification status model for particular notification id
     *
     * @param notificationId
     */
    public findNotificationModelIndexById(notificationId: NotificationIdentifierModel): number {
        let notificationStatusIndex = this.notificationStatusList.findIndex(
            (notificationStatus: NotificationStatusModel) => notificationStatus.notificationId.equalsTo(notificationId),
        );

        if (notificationStatusIndex === -1) {
            this.notificationStatusList.push(new NotificationStatusModel(notificationId, false, '', true, false, []));
            notificationStatusIndex = this.notificationStatusList.length - 1;
        }

        return notificationStatusIndex;
    }

    public getNotificationStatusById(notificationId: NotificationIdentifierModel): NotificationStatusModel {
        const index = this.findNotificationModelIndexById(notificationId);

        return this.notificationStatusList[index];
    }

    public getNotificationStatusByStatusType(
        notificationId: NotificationIdentifierModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): StatusModel | undefined {
        const statusArray = this.getAllNotificationStatus(notificationId);

        return statusArray.find((statusModel: StatusModel) => statusModel.statusCode === statusCode);
    }

    public getEscalated(notificationId: NotificationIdentifierModel): boolean {
        const notificationStatus = this.getNotificationStatusById(notificationId);

        return notificationStatus.escalated;
    }

    public saveEscalated(notificationId: NotificationIdentifierModel, escalated: boolean): Observable<void> {
        const index = this.findNotificationModelIndexById(notificationId);
        this.notificationStatusList[index].escalated = escalated;

        return this.saveNotificationStatusList();
    }

    public getCancelled(notificationId: NotificationIdentifierModel): string {
        const notificationStatus = this.getNotificationStatusById(notificationId);

        return notificationStatus.cancelled;
    }

    public saveCancelled(notificationId: NotificationIdentifierModel, cancelledStatus: string): void {
        const index = this.findNotificationModelIndexById(notificationId);

        this.notificationStatusList[index].cancelled = cancelledStatus;
    }

    public getUnreadIndicator(notificationId: NotificationIdentifierModel): boolean {
        const notificationStatus = this.getNotificationStatusById(notificationId);

        return notificationStatus.unread;
    }

    public saveUnreadIndicator(notificationId: NotificationIdentifierModel, unread: boolean): Observable<void> {
        const index = this.findNotificationModelIndexById(notificationId);

        this.notificationStatusList[index].unread = unread;

        return this.saveNotificationStatusList();
    }

    public getPausedIndicator(notificationId: NotificationIdentifierModel): boolean {
        const notificationStatus = this.getNotificationStatusById(notificationId);

        return notificationStatus.paused;
    }

    public savePausedIndicator(notificationId: NotificationIdentifierModel, paused: boolean): Observable<void> {
        const index = this.findNotificationModelIndexById(notificationId);

        this.notificationStatusList[index].paused = paused;

        return this.saveNotificationStatusList();
    }

    // Methods to save/remove/get cancellation confirmation information for an in-progress notification
    public saveCancellationConfirmationForInprogress(notificationId: NotificationIdentifierModel): Observable<void> {
        return this.storageNotificationStatusService.saveCancellationConfirmationForInprogress(notificationId);
    }

    public removeCancellationConfirmationForInprogress(): void {
        this.storageNotificationStatusService.removeCancellationConfirmationForInprogress();
    }

    public async getCancellationConfirmationForInprogress(
        notificationId: NotificationIdentifierModel,
    ): Promise<boolean> {
        return this.storageNotificationStatusService.getCancellationConfirmationForInprogress(notificationId);
    }

    private sortStatusModel(a: StatusModel, b: StatusModel): number {
        // check if the status of the status models is contained within the status order enum
        if (typeof STATUS_ORDER[<keyof typeof STATUS_ORDER>a.statusCode] === 'undefined') {
            this.log.error(
                `NotificationStatusService: sortStatusModel : Notification is in a state that cannot be sorted: '${a.statusCode}' is unknown!`,
            );

            // give unknown states the highest sorting priority
            return 1;
        }
        if (typeof STATUS_ORDER[<keyof typeof STATUS_ORDER>b.statusCode] === 'undefined') {
            this.log.error(
                `NotificationStatusService: sortStatusModel: Notification is in a state that cannot be sorted: '${b.statusCode}' is unknown!`,
            );

            // give unknown states the highest sorting priority
            return -1;
        }

        // '<keyof typeof STATUS_ORDER>' is an assertion to TypeScript that any statusCode passed
        // into the enum will be a valid key of the enum STATUS_ORDER
        return (
            STATUS_ORDER[<keyof typeof STATUS_ORDER>a.statusCode] -
            STATUS_ORDER[<keyof typeof STATUS_ORDER>b.statusCode]
        );
    }

    /**
     * This method will filter the stored localStorage notification's status and
     * remove those notifications and their status which are no longer present in NDP
     *
     * @param {NotificationModel[]} notificationArray List of notifications from NDPHeaderSet
     */
    public removeOldNotificationsStatus(notificationArray: NotificationModel[]): Observable<void> {
        this.notificationStatusList = this.notificationStatusList.filter((notificationStatus) =>
            notificationArray.some((n) => n.identifier.equalsTo(notificationStatus.notificationId)),
        );

        return this.saveNotificationStatusList();
    }

    public getNotificationByStatusInProgressOrToBeSigned(): NotificationStatusModel | undefined {
        return this.notificationStatusList.find((notificationStatus) => {
            if (notificationStatus.status.length === 0) {
                return false;
            }

            // sort the status models to ensure latest status is obtained
            this.getLatestNotificationStatus(notificationStatus);
            const latestNotificationStatus = notificationStatus.status[notificationStatus.status.length - 1];

            return latestNotificationStatus.isInProgressOrToBeSigned();
        });
    }

    public getNotificationStatusIsPaused(): NotificationStatusModel | undefined {
        return this.notificationStatusList.find((notificationStatus) => notificationStatus.paused);
    }
}
